import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, BrainCircuit, Loader2, RefreshCw, Sparkles, Mail, Trash2,
  Plus, CheckCircle2, CalendarDays, Users, TrendingUp, FileText, X, Send,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('eventism_token')}` });

const STATUS_STYLE = {
  added: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
  invited: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  opened: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
  processing: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  submitted: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  published: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  error: 'bg-red-500/15 text-red-300 border-red-500/30',
};

function TemplateSettings({ settings, onSaved }) {
  const [subject, setSubject] = useState(settings.outreach_subject || '');
  const [html, setHtml] = useState(settings.outreach_html || '');
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(!settings.template_approved);

  const save = async (approve) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/editorial-bot/settings`, {
        outreach_subject: subject, outreach_html: html,
        ...(approve ? { template_approved: true } : {}),
      }, { headers: authHeaders() });
      toast.success(approve
        ? 'Template approved — the bot now sends invites automatically when you add a partner.'
        : 'Template saved');
      onSaved();
      if (approve) setOpen(false);
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className={`mb-6 ${settings.template_approved ? 'border-emerald-500/30' : 'border-amber-500/40 bg-amber-500/5'}`} data-testid="eb-template-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold flex items-center gap-2">
            <Mail className="h-4 w-4 text-fuchsia-300" /> Outreach email template
            {settings.template_approved
              ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]">APPROVED — AUTO-SEND ON</Badge>
              : <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px]">NEEDS YOUR APPROVAL</Badge>}
          </h2>
          <button onClick={() => setOpen((o) => !o)} className="text-xs text-muted-foreground hover:text-foreground underline" data-testid="eb-template-toggle">
            {open ? 'Collapse' : 'Review / edit'}
          </button>
        </div>
        {open && (
          <>
            <p className="text-[11px] text-muted-foreground">
              Merge fields: <code className="text-fuchsia-300">{'{{name}} {{city}} {{expertise}} {{proposed_title}} {{link}}'}</code>. Approve once — after that the bot sends invites automatically the moment you add a partner. Sent from <b>{settings.from_email}</b>.
            </p>
            <div className="space-y-1">
              <Label className="text-xs">Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="eb-template-subject" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">HTML body</Label>
              <Textarea rows={8} value={html} onChange={(e) => setHtml(e.target.value)} className="font-mono text-[11px]" data-testid="eb-template-html" />
            </div>
            <div className="rounded-lg border border-border/60 bg-white p-3 max-h-64 overflow-y-auto" data-testid="eb-template-preview">
              <div dangerouslySetInnerHTML={{ __html: html
                .replaceAll('{{name}}', 'DJ Squizzy Taylor').replaceAll('{{city}}', 'Denver')
                .replaceAll('{{expertise}}', "you run the city's best Hip Hop & R&B nights")
                .replaceAll('{{proposed_title}}', "Denver's Best Hip Hop & R&B Nightclubs")
                .replaceAll('{{link}}', '#preview') }} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => save(false)} disabled={saving} data-testid="eb-template-save">Save</Button>
              {!settings.template_approved && (
                <Button size="sm" onClick={() => save(true)} disabled={saving}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0"
                  data-testid="eb-template-approve">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                  Approve — enable auto-send
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function IntelSection({ intel, cities, onRefresh, refreshing }) {
  return (
    <div className="mb-8">
      <h2 className="text-sm font-bold mb-2 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-fuchsia-300" /> City intelligence
      </h2>
      <div className="grid lg:grid-cols-2 gap-4" data-testid="eb-intel-list">
        {cities.map((city) => {
          const doc = intel.find((i) => i.city === city.toLowerCase());
          const brief = doc?.brief || {};
          return (
            <Card key={city} className="border-border/60" data-testid={`eb-intel-${city.toLowerCase()}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="font-bold font-heading">{city}</p>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] border-fuchsia-500/40 text-fuchsia-300"
                    onClick={() => onRefresh(city)} disabled={refreshing === city}
                    data-testid={`eb-refresh-intel-${city.toLowerCase()}`}>
                    {refreshing === city ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                    Refresh intel
                  </Button>
                </div>
                {!doc ? (
                  <p className="text-xs text-muted-foreground">No intel yet — hit Refresh. Takes ~2-3 min (mines Google reviews + your sales signals, then Claude writes the trend brief).</p>
                ) : (
                  <>
                    <p className="text-[10px] text-muted-foreground">
                      Updated {new Date(doc.updated_at).toLocaleString()} · {doc.review_venues_mined} venues&apos; reviews mined
                      {doc.review_error ? <span className="text-amber-300"> · ⚠ {doc.review_error}</span> : null}
                    </p>
                    {brief.summary_html && (
                      <div className="text-xs text-muted-foreground leading-relaxed prose-invert max-h-40 overflow-y-auto"
                        dangerouslySetInnerHTML={{ __html: brief.summary_html }} />
                    )}
                    {(brief.rising_vibes || []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {brief.rising_vibes.map((v) => (
                          <span key={v} className="text-[10px] rounded-full border border-fuchsia-500/40 bg-fuchsia-500/10 px-2 py-0.5 text-fuchsia-200">↑ {v}</span>
                        ))}
                      </div>
                    )}
                    {(brief.audience_profiles || []).slice(0, 3).map((a, i) => (
                      <div key={i} className="text-[11px] text-muted-foreground">
                        <span className="text-foreground font-semibold">{a.category}</span> — {a.who}
                        <Badge className={`ml-1 text-[9px] ${a.buy_intent === 'high' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30'}`}>{a.buy_intent} intent</Badge>
                      </div>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
        {cities.length === 0 && (
          <Card className="border-dashed"><CardContent className="p-6 text-xs text-muted-foreground">Launch a city in EventismBot first — the editorial brain follows the same cities.</CardContent></Card>
        )}
      </div>
    </div>
  );
}

function CalendarSection({ calendar, cities, onGenerate, onDraft, onDelete, generating }) {
  const proposed = calendar.filter((c) => c.status === 'proposed');
  const drafted = calendar.filter((c) => c.status !== 'proposed');
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-bold flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-amber-300" /> Content calendar
          <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px]">{proposed.length} proposed</Badge>
        </h2>
        <div className="flex gap-1.5">
          {cities.map((city) => (
            <Button key={city} size="sm" variant="outline" className="h-7 text-[11px] border-amber-500/40 text-amber-300"
              onClick={() => onGenerate(city)} disabled={generating === city}
              data-testid={`eb-gen-calendar-${city.toLowerCase()}`}>
              {generating === city ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
              Plan {city}
            </Button>
          ))}
        </div>
      </div>
      <Card className="border-border/60">
        <CardContent className="p-0 divide-y divide-border/50 max-h-[420px] overflow-y-auto" data-testid="eb-calendar-list">
          {calendar.length === 0 && (
            <p className="p-4 text-xs text-muted-foreground">No plan yet — refresh a city&apos;s intel, then hit &quot;Plan {'{city}'}&quot; to generate the strategy-ranked guide pipeline.</p>
          )}
          {[...proposed, ...drafted].map((item) => (
            <div key={item.item_id} className="p-3 flex items-start gap-3" data-testid={`eb-cal-${item.item_id}`}>
              <span className="text-lg font-bold text-amber-300/70 tabular-nums w-6 shrink-0">{item.priority}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{item.title}
                  <Badge className="ml-2 text-[9px] bg-indigo-500/15 text-indigo-300 border-indigo-500/30">{item.cluster}</Badge>
                  {item.status !== 'proposed' && <Badge className="ml-1 text-[9px] bg-emerald-500/15 text-emerald-300 border-emerald-500/30">{item.status.toUpperCase()}</Badge>}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">{item.reason}</p>
                {item.dream_author && <p className="text-[10px] text-fuchsia-300/80 mt-0.5">✦ Dream author: {item.dream_author}</p>}
              </div>
              {item.status === 'proposed' ? (
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="outline" className="h-7 text-[11px] border-fuchsia-500/40 text-fuchsia-300"
                    onClick={() => onDraft(item)} data-testid={`eb-draft-${item.item_id}`}>
                    <FileText className="h-3 w-3 mr-1" /> Draft it
                  </Button>
                  <button onClick={() => onDelete(item)} className="p-1 text-red-400 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ) : (
                <a href="/admin/guides" className="text-[11px] text-emerald-300 hover:underline shrink-0">Open in guides →</a>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function PartnersSection({ partners, templateApproved, onAdded, onSend, onDelete }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', city: 'denver', expertise: '', proposed_title: '', proposed_category: '', instagram: '' });
  const [holdInvite, setHoldInvite] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.name || !form.email || !form.proposed_title) return toast.error('Name, email, and proposed guide title are required');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/editorial-bot/partners`, { ...form, hold_invite: holdInvite }, { headers: authHeaders() });
      if (r.data.held) {
        toast.success(`${form.name} added — invite HELD. Hit "Send invite" on their row whenever you're ready.`);
      } else if (r.data.invite_sent) {
        toast.success(`${form.name} added — invite email sent automatically ✉️`);
      } else if (!r.data.template_approved) {
        toast.warning(`${form.name} added, but the invite was NOT sent — approve the outreach template first.`);
      } else {
        toast.warning(`${form.name} added but the email failed to send — use the Send button to retry.`);
      }
      setForm({ name: '', email: '', city: form.city, expertise: '', proposed_title: '', proposed_category: '', instagram: '' });
      setShowForm(false);
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-bold flex items-center gap-2">
          <Users className="h-4 w-4 text-sky-300" /> Guest author pipeline
          <Badge className="bg-sky-500/15 text-sky-300 border-sky-500/30 text-[10px]">{partners.length}</Badge>
        </h2>
        <Button size="sm" className="h-8 text-xs bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 text-white border-0"
          onClick={() => setShowForm((s) => !s)} data-testid="eb-add-partner-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Recruit author
        </Button>
      </div>

      {showForm && (
        <Card className="border-fuchsia-500/30 bg-fuchsia-500/5 mb-3" data-testid="eb-partner-form">
          <CardContent className="p-4 grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="DJ Squizzy Taylor" data-testid="eb-partner-name" /></div>
            <div className="space-y-1"><Label className="text-xs">Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="squizzy@..." data-testid="eb-partner-email" /></div>
            <div className="space-y-1"><Label className="text-xs">City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="eb-partner-city" /></div>
            <div className="space-y-1"><Label className="text-xs">Instagram (optional)</Label>
              <Input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="https://instagram.com/..." data-testid="eb-partner-instagram" /></div>
            <div className="sm:col-span-2 space-y-1"><Label className="text-xs">Why them (personalizes the email)</Label>
              <Input value={form.expertise} onChange={(e) => setForm({ ...form, expertise: e.target.value })} placeholder="you run the best Hip Hop & R&B nights in Denver" data-testid="eb-partner-expertise" /></div>
            <div className="space-y-1"><Label className="text-xs">Proposed guide title</Label>
              <Input value={form.proposed_title} onChange={(e) => setForm({ ...form, proposed_title: e.target.value })} placeholder="Denver's Best Hip Hop & R&B Nightclubs" data-testid="eb-partner-title" /></div>
            <div className="space-y-1"><Label className="text-xs">Category label</Label>
              <Input value={form.proposed_category} onChange={(e) => setForm({ ...form, proposed_category: e.target.value })} placeholder="Hip Hop & R&B Nightclubs" data-testid="eb-partner-category" /></div>
            <div className="sm:col-span-2 flex items-center justify-between gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="eb-partner-hold">
                <input type="checkbox" checked={holdInvite} onChange={(e) => setHoldInvite(e.target.checked)} className="accent-fuchsia-500" />
                Hold the invite — I&apos;ll send it manually later
              </label>
              <Button size="sm" onClick={submit} disabled={submitting}
                className="bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 text-white border-0"
                data-testid="eb-partner-submit">
                {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                {holdInvite ? 'Add (hold invite)' : templateApproved ? 'Add & auto-send invite' : 'Add (approve template to auto-send)'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-border/60">
        <CardContent className="p-0 divide-y divide-border/50 max-h-[420px] overflow-y-auto" data-testid="eb-partner-list">
          {partners.length === 0 && (
            <p className="p-4 text-xs text-muted-foreground">No authors recruited yet. Add a local tastemaker — the bot emails them a magic link, edits their submission, and publishes it under their byline.</p>
          )}
          {partners.map((p) => (
            <div key={p.partner_id} className="p-3 flex items-center gap-3" data-testid={`eb-partner-${p.partner_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{p.name}
                  <Badge className={`ml-2 text-[9px] ${STATUS_STYLE[p.status] || STATUS_STYLE.added}`}>{p.status.toUpperCase()}</Badge>
                  {p.reminders_sent > 0 && <span className="text-[10px] text-muted-foreground ml-1">· {p.reminders_sent} reminder{p.reminders_sent > 1 ? 's' : ''}</span>}
                </div>
                <p className="text-[11px] text-muted-foreground truncate">&quot;{p.proposed_title}&quot; · {p.city} · {p.email}</p>
                {(p.promo_earned_cents > 0 || p.promo_clicks > 0) && (
                  <p className="text-[10px] text-emerald-300 mt-0.5" data-testid={`eb-earnings-${p.partner_id}`}>
                    💸 ${((p.promo_earned_cents || 0) / 100).toFixed(2)} earned · {p.promo_sales || 0} sales · {p.promo_clicks || 0} reads{p.account_claimed ? ' · account claimed' : ''}
                  </p>
                )}
              </div>
              {p.guide_url && (
                <a href={p.guide_url} target="_blank" rel="noreferrer" className="text-[11px] text-emerald-300 hover:underline shrink-0">Live guide →</a>
              )}
              {p.status !== 'published' && (
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onSend(p)} data-testid={`eb-send-${p.partner_id}`}>
                  <Mail className="h-3 w-3 mr-1" /> {p.status === 'added' ? 'Send invite' : 'Remind'}
                </Button>
              )}
              <button onClick={() => onDelete(p)} className="p-1 text-red-400 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminEditorialBot() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(null);
  const [generating, setGenerating] = useState(null);

  const fetchOverview = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/editorial-bot/overview`, { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load EditorialBot');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const refreshIntel = async (city) => {
    setRefreshing(city);
    try {
      const r = await axios.post(`${API}/admin/editorial-bot/intel/refresh/${city}`, {}, { headers: authHeaders() });
      toast.success(r.data?.message || 'Intel refresh started');
      setTimeout(() => fetchOverview(true), 150000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh failed');
    } finally {
      setTimeout(() => setRefreshing(null), 3000);
    }
  };

  const genCalendar = async (city) => {
    setGenerating(city);
    try {
      const r = await axios.post(`${API}/admin/editorial-bot/calendar/generate/${city}`, {}, { headers: authHeaders() });
      toast.success(`${r.data.created} guide idea${r.data.created === 1 ? '' : 's'} added to the calendar`);
      fetchOverview(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generate failed');
    } finally {
      setGenerating(null);
    }
  };

  const draftItem = async (item) => {
    const t = toast.loading(`Drafting "${item.title}"…`);
    try {
      await axios.post(`${API}/admin/editorial-bot/calendar/${item.item_id}/draft`, {}, { headers: authHeaders(), timeout: 120000 });
      toast.success('Draft created — it\'s in your guides queue.', { id: t });
      fetchOverview(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Draft failed', { id: t });
    }
  };

  const deleteItem = async (item) => {
    await axios.delete(`${API}/admin/editorial-bot/calendar/${item.item_id}`, { headers: authHeaders() });
    fetchOverview(true);
  };

  const sendInvite = async (p) => {
    try {
      const r = await axios.post(`${API}/admin/editorial-bot/partners/${p.partner_id}/send`, {}, { headers: authHeaders() });
      toast.success(r.data.reminder ? `Reminder sent to ${p.name}` : `Invite sent to ${p.name}`);
      fetchOverview(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    }
  };

  const deletePartner = async (p) => {
    if (!window.confirm(`Remove ${p.name} from the pipeline?`)) return;
    await axios.delete(`${API}/admin/editorial-bot/partners/${p.partner_id}`, { headers: authHeaders() });
    fetchOverview(true);
  };

  if (user?.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/guides')} data-testid="eb-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Guides
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
              <BrainCircuit className="h-6 w-6 text-fuchsia-400" /> EventismEditorialBot
            </h1>
            <p className="text-sm text-muted-foreground">
              Learns each city (reviews · trends · your buy-intent data) → plans the listicles that capture demand before it converts → recruits local tastemakers to author them.
            </p>
          </div>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-fuchsia-400" /></div>
        ) : data && (
          <>
            <TemplateSettings settings={data.settings} onSaved={() => fetchOverview(true)} />
            <IntelSection intel={data.intel} cities={data.cities} onRefresh={refreshIntel} refreshing={refreshing} />
            <CalendarSection calendar={data.calendar} cities={data.cities} onGenerate={genCalendar} onDraft={draftItem} onDelete={deleteItem} generating={generating} />
            <PartnersSection partners={data.partners} templateApproved={data.settings.template_approved} onAdded={() => fetchOverview(true)} onSend={sendInvite} onDelete={deletePartner} />
          </>
        )}
      </div>
    </div>
  );
}
